import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type NumericRange = {
  min: number;
  max: number;
};

const toSafeNumber = (value: number) => (Number.isFinite(value) ? value : 0);

const getRangeBounds = (values: number[]): NumericRange => {
  if (!values.length) {
    return { min: 0, max: 0 };
  }

  return {
    min: Math.min(...values),
    max: Math.max(...values),
  };
};

const clampRange = (range: NumericRange, bounds: NumericRange): NumericRange => {
  const nextMin = Math.min(Math.max(range.min, bounds.min), bounds.max);
  const nextMax = Math.max(nextMin, Math.min(range.max, bounds.max));
  return { min: nextMin, max: nextMax };
};

export const useNumericRangeFilter = (values: number[], externalBounds?: NumericRange) => {
  const hasExternalBounds = externalBounds != null;
  const externalMin = externalBounds?.min;
  const externalMax = externalBounds?.max;

  const bounds = useMemo(
    () =>
      hasExternalBounds && externalMin != null && externalMax != null
        ? { min: externalMin, max: externalMax }
        : getRangeBounds(values),
    [externalMax, externalMin, hasExternalBounds, values]
  );
  const [filter, setFilter] = useState<NumericRange | null>(null);
  const previousBoundsRef = useRef<NumericRange>(bounds);

  useEffect(() => {
    if (!values.length && !hasExternalBounds) {
      setFilter(null);
      previousBoundsRef.current = bounds;
      return;
    }

    const previousBounds = previousBoundsRef.current;
    setFilter((prev) => {
      if (!prev) {
        return bounds;
      }

      const wasAtPreviousBounds =
        prev.min === previousBounds.min && prev.max === previousBounds.max;
      if (wasAtPreviousBounds) {
        return bounds;
      }

      const next = clampRange(prev, bounds);
      if (next.min === prev.min && next.max === prev.max) {
        return prev;
      }

      return next;
    });

    previousBoundsRef.current = bounds;
  }, [values.length, bounds.max, bounds.min, hasExternalBounds]);

  const matches = useCallback(
    (value: number) => {
      if (!filter) {
        return true;
      }

      const safeValue = toSafeNumber(value);
      return safeValue >= filter.min && safeValue <= filter.max;
    },
    [filter]
  );

  return {
    bounds,
    value: filter ?? bounds,
    setValue: setFilter,
    matches,
  };
};
